const MIN_BLOCK_LEN = 8;
const MIN_LINE_LEN = 3;

const HEADING_LINE_RE = /^(#{1,6})\s+(.+)$/;
const CAROUSEL_CHROME_RE =
	/^(previous|next|prev|slide\s*\d+|swiper-button(?:-next|-prev)?|slick-(?:prev|next))$/i;

export function normalizeBlock(block) {
	return block.replace(/\s+/g, " ").trim().toLowerCase();
}

export function normalizeLine(line) {
	return line
		.replace(/\s+/g, " ")
		.replace(/\\([*_\-[\]()])/g, "$1")
		.replace(/\*/g, "")
		.trim()
		.toLowerCase();
}

export function anchorKeyForBlock(block) {
	return normalizeBlock(block).slice(0, 240);
}

export function prepareMarkdownForBlocks(md) {
	if (!md.trim()) return "";
	return md
		.replace(/\r\n/g, "\n")
		.replace(/\n{3,}/g, "\n\n")
		.replace(/(?<=[.!?]"?)(?="[A-Z])/g, "\n\n")
		.replace(/(?<=")(?=")/g, "\n\n")
		.replace(/^(#{1,6}\s)/gm, "\n\n$1")
		.replace(/^\n+/, "")
		.trim();
}

export function splitMarkdownBlocks(md) {
	const prepared = prepareMarkdownForBlocks(md);
	return prepared
		.split(/\n{2,}/)
		.map((b) => b.trim())
		.filter((b) => b.length >= MIN_BLOCK_LEN);
}

function splitContentLines(content) {
	if (!content.trim()) return [];
	return content.split("\n");
}

function headingAnchor(headingLine) {
	const match = headingLine.match(HEADING_LINE_RE);
	if (!match) return anchorKeyForBlock(headingLine);
	return normalizeBlock(match[2]);
}

function isNearDuplicateSection(a, b) {
	const na = normalizeBlock(a);
	const nb = normalizeBlock(b);
	if (na === nb) return true;
	if (na.length < 24 || nb.length < 24) return false;
	const prefixLen = Math.min(120, na.length, nb.length);
	return na.slice(0, prefixLen) === nb.slice(0, prefixLen);
}

export function extractSections(md) {
	if (!md.trim()) return [];

	const prepared = prepareMarkdownForBlocks(md);
	const lines = prepared.split("\n");
	const sections = [];

	let currentHeading = "";
	let currentLines = [];
	let order = 0;

	const flush = () => {
		const content = currentLines.join("\n").trim();
		if (!content) return;

		const anchor = currentHeading ? headingAnchor(currentHeading) : "__preamble__";
		const section = {
			anchor,
			heading: currentHeading,
			content,
			order: order++,
		};

		const last = sections[sections.length - 1];
		if (
			last &&
			last.anchor === section.anchor &&
			isNearDuplicateSection(last.content, section.content)
		) {
			return;
		}

		const sameAnchorCount = sections.filter((s) => s.anchor === section.anchor).length;
		if (sameAnchorCount > 0) {
			section.anchor = `${section.anchor}#${sameAnchorCount + 1}`;
		}

		sections.push(section);
	};

	for (const line of lines) {
		if (HEADING_LINE_RE.test(line)) {
			flush();
			currentHeading = line;
			currentLines = [line];
		} else {
			currentLines.push(line);
		}
	}
	flush();

	return sections;
}

function tokenOverlapRatio(a, b) {
	const tokensA = a.split(/\s+/).filter((t) => t.length > 1);
	const tokensB = b.split(/\s+/).filter((t) => t.length > 1);
	if (tokensA.length === 0 || tokensB.length === 0) return a === b ? 1 : 0;

	const setB = new Set(tokensB);
	const intersection = tokensA.filter((t) => setB.has(t)).length;
	const union = new Set([...tokensA, ...tokensB]).size;
	return intersection / union;
}

function fuzzyLineMatch(norm, otherNorm) {
	if (norm === otherNorm) return true;
	if (!norm || !otherNorm) return norm === otherNorm;

	if (norm.length < MIN_LINE_LEN || otherNorm.length < MIN_LINE_LEN) {
		return norm === otherNorm;
	}

	if (tokenOverlapRatio(norm, otherNorm) >= 0.85) return true;

	const ratio = Math.min(norm.length, otherNorm.length) / Math.max(norm.length, otherNorm.length);
	if (ratio >= 0.9 && (norm.includes(otherNorm) || otherNorm.includes(norm))) {
		return true;
	}

	return false;
}

function isCarouselChrome(line) {
	const norm = normalizeLine(line.trim());
	if (!norm) return false;
	return CAROUSEL_CHROME_RE.test(norm);
}

function blocksEquivalent(blockA, blockB) {
	if (blockA.length !== blockB.length) return false;
	return blockA.every((lineA, index) =>
		fuzzyLineMatch(normalizeLine(lineA), normalizeLine(blockB[index]))
	);
}

function dedupeConsecutiveLines(lines) {
	const result = [];
	for (const line of lines) {
		if (result.length === 0) {
			result.push(line);
			continue;
		}
		const previous = result[result.length - 1];
		if (fuzzyLineMatch(normalizeLine(line), normalizeLine(previous))) {
			continue;
		}
		result.push(line);
	}
	return result;
}

function collapseRepeatingSequence(lines) {
	const total = lines.length;
	if (total < 4) return lines;

	for (let blockSize = Math.floor(total / 2); blockSize >= 2; blockSize--) {
		if (total % blockSize !== 0) continue;

		const repeats = total / blockSize;
		if (repeats < 2) continue;

		const block = lines.slice(0, blockSize);
		let allMatch = true;

		for (let repeat = 1; repeat < repeats; repeat++) {
			const chunk = lines.slice(repeat * blockSize, (repeat + 1) * blockSize);
			if (!blocksEquivalent(block, chunk)) {
				allMatch = false;
				break;
			}
		}

		if (allMatch) return block;
	}

	return lines;
}

function dedupeRepeatingBlock(lines) {
	if (lines.length < 4) return lines;

	const maxPrefix = Math.min(lines.length - 4, 4);
	for (let prefixLen = 0; prefixLen <= maxPrefix; prefixLen++) {
		const prefix = lines.slice(0, prefixLen);
		const remainder = lines.slice(prefixLen);
		const collapsed = collapseRepeatingSequence(remainder);

		if (collapsed.length < remainder.length) {
			return [...prefix, ...collapsed];
		}
	}

	return collapseRepeatingSequence(lines);
}

export function canonicalizeSectionContent(content) {
	if (!content?.trim()) return [];

	let lines = splitContentLines(content)
		.map((line) => line.trim())
		.filter((line) => line.length > 0 && !isCarouselChrome(line));

	lines = dedupeConsecutiveLines(lines);
	lines = dedupeRepeatingBlock(lines);
	return lines;
}

function sectionContentSignature(content) {
	return canonicalizeSectionContent(content)
		.map((line) => normalizeLine(line))
		.filter((norm) => norm.length >= MIN_LINE_LEN)
		.sort()
		.join("\0");
}

function findBagKeyForNorm(norm, bag) {
	for (const key of Object.keys(bag)) {
		if (fuzzyLineMatch(norm, key)) return key;
	}
	return null;
}

function buildLineBag(lines) {
	const bag = {};

	for (const line of lines) {
		const norm = normalizeLine(line);
		if (norm.length < MIN_LINE_LEN) continue;

		const key = findBagKeyForNorm(norm, bag) ?? norm;
		bag[key] = (bag[key] || 0) + 1;
	}

	return bag;
}

function findBagMatch(norm, baseBag) {
	for (const key of Object.keys(baseBag)) {
		if (baseBag[key] > 0 && fuzzyLineMatch(norm, key)) {
			return key;
		}
	}
	return null;
}

function baseChromeNorms(baseContent) {
	const norms = new Set();

	for (const line of splitContentLines(baseContent)) {
		const trimmed = line.trim();
		if (trimmed.length < MIN_LINE_LEN || !isCarouselChrome(trimmed)) continue;
		norms.add(normalizeLine(trimmed));
	}

	return norms;
}

function isChromeOnlyInTarget(baseContent, line) {
	const trimmed = line.trim();
	if (trimmed.length < MIN_LINE_LEN || !isCarouselChrome(trimmed)) return false;

	const norm = normalizeLine(trimmed);
	const baseNorms = baseChromeNorms(baseContent);
	return ![...baseNorms].some((baseNorm) => fuzzyLineMatch(norm, baseNorm));
}

function collectChromeOnlyInTarget(baseContent, targetContent) {
	const added = new Set();

	for (const line of splitContentLines(targetContent)) {
		if (isChromeOnlyInTarget(baseContent, line)) {
			added.add(line);
		}
	}

	return added;
}

export function linesAddedVsBase(baseContent, targetContent) {
	if (!targetContent.trim()) return new Set();
	if (!baseContent?.trim()) {
		return new Set(
			splitContentLines(targetContent).filter((line) => line.trim().length >= MIN_LINE_LEN)
		);
	}

	if (sectionContentSignature(baseContent) === sectionContentSignature(targetContent)) {
		return collectChromeOnlyInTarget(baseContent, targetContent);
	}

	const baseBag = buildLineBag(canonicalizeSectionContent(baseContent));
	const added = new Set();

	for (const line of splitContentLines(targetContent)) {
		const trimmed = line.trim();
		if (trimmed.length < MIN_LINE_LEN) continue;

		if (isCarouselChrome(trimmed)) {
			if (isChromeOnlyInTarget(baseContent, line)) {
				added.add(line);
			}
			continue;
		}

		const norm = normalizeLine(trimmed);
		const matchKey = findBagMatch(norm, baseBag);

		if (matchKey) {
			baseBag[matchKey]--;
			continue;
		}

		added.add(line);
	}

	return added;
}

export function linesAddedVsAi(aiContent, targetContent) {
	return linesAddedVsBase(aiContent, targetContent);
}

export function linesAddedVsHuman(humanContent, scrollContent) {
	return linesAddedVsBase(humanContent, scrollContent);
}

export function baseSectionAnchor(anchor) {
	return anchor.replace(/#\d+$/, "");
}

export function findSectionByAnchor(sections, anchor) {
	const exact = sections.find((s) => s.anchor === anchor);
	if (exact) return exact;

	const base = baseSectionAnchor(anchor);
	const related = sections.filter(
		(s) => s.anchor === base || s.anchor.startsWith(`${base}#`)
	);
	if (related.length === 0) {
		return sections.find((s) => baseSectionAnchor(s.anchor) === base) ?? null;
	}

	const suffixMatch = anchor.match(/#(\d+)$/);
	if (suffixMatch) {
		const index = Number(suffixMatch[1]) - 1;
		return related[index] ?? related[0];
	}

	return related[0];
}

export function sectionsDifferFromAi(aiContent, targetContent) {
	if (!aiContent?.trim()) return true;
	if (!targetContent?.trim()) return false;
	return sectionContentSignature(aiContent) !== sectionContentSignature(targetContent);
}

export function sectionsWithAddedLines(baseMd, targetMd) {
	const baseSections = extractSections(baseMd);
	let count = 0;

	for (const section of extractSections(targetMd)) {
		const baseSec = findSectionByAnchor(baseSections, section.anchor);
		if (!baseSec) {
			count++;
			continue;
		}
		if (linesAddedVsBase(baseSec.content, section.content).size > 0) {
			count++;
		}
	}

	return count;
}

export function countAddedBlocks(baseMd, targetMd) {
	return sectionsWithAddedLines(baseMd, targetMd);
}

function isLineAdded(line, addedLines) {
	const norm = normalizeLine(line);
	if (!norm || norm.length < MIN_LINE_LEN) return false;

	for (const added of addedLines) {
		if (fuzzyLineMatch(norm, normalizeLine(added))) return true;
	}
	return false;
}

export function buildDualLensLineSegments(
	markdown,
	humanAdded,
	scrollAdded = new Set()
) {
	if (!markdown.trim()) {
		return [];
	}

	if (humanAdded.size === 0 && scrollAdded.size === 0) {
		return [{ kind: "text", content: markdown }];
	}

	const lines = markdown.split("\n");
	const segments = [];
	let textBuf = [];
	let addedBuf = [];
	let addedVariant = null;

	const flushText = () => {
		if (textBuf.length === 0) return;
		segments.push({ kind: "text", content: textBuf.join("\n") });
		textBuf = [];
	};

	const flushAdded = () => {
		if (addedBuf.length === 0 || !addedVariant) return;
		segments.push({ kind: "added", content: addedBuf.join("\n"), variant: addedVariant });
		addedBuf = [];
		addedVariant = null;
	};

	for (const line of lines) {
		const isScrollOnly = isLineAdded(line, scrollAdded);
		const isHuman = isLineAdded(line, humanAdded);

		const variant = isScrollOnly ? "advanced" : isHuman ? "human" : null;

		if (variant) {
			flushText();
			if (addedVariant && addedVariant !== variant) {
				flushAdded();
			}
			addedVariant = variant;
			addedBuf.push(line);
		} else {
			flushAdded();
			textBuf.push(line);
		}
	}

	flushAdded();
	flushText();

	return segments.length > 0 ? segments : [{ kind: "text", content: markdown }];
}
